import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';

export interface UseTotpSetupFlowOptions {
  /** Called after recovery codes are acknowledged — two-factor is fully on. */
  onEnabled?: () => void;
}

export const useTotpSetupFlow = (options: UseTotpSetupFlowOptions = {}) => {
  const { t } = useTranslation('security');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);
  const user = useAuthStore((state) => state.user);

  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const status = useQuery({
    queryKey: ['totp', 'status'],
    queryFn: () =>
      api.get<{ enabled: boolean; recoveryCodesRemaining: number }>('/auth/totp', {
        withoutChurch: true,
      }),
  });

  const setup = useMutation({
    mutationFn: () =>
      api.post<{ otpauthUrl: string; secret: string }>('/auth/totp/setup', undefined, {
        withoutChurch: true,
      }),
    onSuccess: (result) => {
      setSetupUrl(result.otpauthUrl);
      setRecoveryCodes(null);
      setCode('');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const confirm = useMutation({
    mutationFn: (submitCode?: string) =>
      api.post<{ recoveryCodes: string[] }>(
        '/auth/totp/confirm',
        { code: submitCode ?? code },
        { withoutChurch: true },
      ),
    onSuccess: async (result) => {
      setRecoveryCodes(result.recoveryCodes);
      setSetupUrl(null);
      setCode('');
      toast.success(t('totp.enabledToast'));
      void queryClient.invalidateQueries({ queryKey: ['totp', 'status'] });
      const me = await api.get<NonNullable<typeof user>>('/auth/me', { withoutChurch: true });
      setUser(me);
      await useChurchStore.getState().refreshCurrent();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const enabled = status.data?.enabled ?? user?.isTotpEnabled ?? false;

  const reset = useCallback(() => {
    setSetupUrl(null);
    setCode('');
    setRecoveryCodes(null);
  }, []);

  const onEnabledRef = useRef(options.onEnabled);
  onEnabledRef.current = options.onEnabled;

  const acknowledgeRecoveryCodes = useCallback(() => {
    setRecoveryCodes(null);
    onEnabledRef.current?.();
  }, []);

  return {
    enabled,
    statusLoading: status.isLoading,
    setupUrl,
    code,
    setCode,
    recoveryCodes,
    reset,
    startSetup: setup.mutate,
    isStarting: setup.isPending,
    confirmSetup: (submitCode?: string) => confirm.mutate(submitCode),
    isConfirming: confirm.isPending,
    acknowledgeRecoveryCodes,
  };
};
